import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaBackward } from "react-icons/fa";
import Logo from "../assest/l.png";
import { resetPassword } from "../utils/api";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validToken, setValidToken] = useState(false);

  useEffect(() => {
    // You might want to verify the token here first
    // For now we'll assume it's valid when the page loads
    setValidToken(true);
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (password !== confirmPassword) {
      return setError("Passwords do not match");
    }

    setLoading(true);

    try {
      const response = await resetPassword(token, password);
      if (response.success) {
        setSuccess(true);
        setTimeout(() => navigate("/login"), 3000);
      } else {
        setError(response.message || "Failed to reset password");
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (!validToken) {
    return (
      <div className="relative w-full h-[100dvh] lg:h-screen bg-gradient-to-br from-red-500 via-red-300 to-yellow-100 flex items-center justify-center p-4 overflow-hidden">
        <div className="w-full h-full bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row">
          <div className="md:w-1/2 w-full px-8 py-6 h-full flex flex-col justify-center items-center">
            <h2 className="text-3xl font-bold text-center text-gray-800 mt-4 mb-6">
              Invalid Token
            </h2>
            <p className="text-gray-600 mb-6">
              The password reset link is invalid or has expired.
            </p>
            <Link
              to="/forgot-password"
              className="text-indigo-600 hover:underline"
            >
              Request a new reset link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full lg:h-screen bg-gradient-to-br from-red-500 via-red-300 to-yellow-100 flex items-center justify-center p-4 overflow-hidden">
      {/* Logo */}
      <div className="absolute top-10 left-10">
        <Link to="/">
          <img src={Logo} alt="Tactos Logo" className="w-28" />
        </Link>
      </div>

      {/* Auth Card */}
      <div className="w-full lg:h-full bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row py-36 lg:py-0">
        <div className="md:w-1/2 w-full px-8 lg:py-6 lg:h-full flex flex-col justify-center items-center">
          <h2 className="text-3xl font-bold text-center text-gray-800 mt-4 mb-6">
            Reset Password
          </h2>

          {success ? (
            <div className="text-center mt-6">
              <h3 className="text-2xl font-semibold text-green-600 mb-4">
                Password Updated!
              </h3>
              <p className="text-gray-600 mb-4">
                Your password has been successfully updated.
              </p>
              <p className="text-gray-600">
                Redirecting to login page...
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="space-y-5 text-sm w-full lg:w-1/2 mx-auto"
            >
              {error && (
                <motion.div
                  initial={{ y: -10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.2 }}
                  className="bg-red-100 text-red-700 p-3 rounded text-sm"
                >
                  {error}
                </motion.div>
              )}

              <div className="flex flex-col">
                <label
                  htmlFor="password"
                  className="text-gray-700 font-medium mb-1"
                >
                  New Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-gray-200 placeholder-gray-500 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 border-none"
                  placeholder="Enter new password"
                  required
                  minLength="6"
                />
              </div>

              <div className="flex flex-col">
                <label
                  htmlFor="confirmPassword"
                  className="text-gray-700 font-medium mb-1"
                >
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-gray-200 placeholder-gray-500 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 border-none"
                  placeholder="Confirm new password"
                  required
                  minLength="6"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full px-6 py-2 bg-[#4b154c] text-white rounded-full hover:bg-purple-700 text-sm ${
                  loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {loading ? "Updating..." : "Update Password"}
              </button>
            </form>
          )}
        </div>

        <div className="md:w-1/2 hidden lg:block relative overflow-hidden">
          <img
            src="/login_img.jpg"
            alt="Startup Illustration"
            className="w-full lg:h-full object-cover"
          />
          <Link
            to="/"
            className="absolute top-4 right-4 flex items-center gap-2 text-black font-bold"
          >
            <FaBackward /> Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;